import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import supabase from '../lib/supabase'

const PORTAL_SESSION_KEY = 'codevaro_portal_session'

export default function ClientPortalProjectDetails() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [sessionToken, setSessionToken] = useState(localStorage.getItem(PORTAL_SESSION_KEY) || '')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [project, setProject] = useState(null)
  const [files, setFiles] = useState([])
  const [updates, setUpdates] = useState([])

  useEffect(() => {
    if (sessionToken && projectId) {
      loadProject()
    }
  }, [sessionToken, projectId])

  async function loadProject() {
    try {
      setLoading(true)
      setError('')

      const [projectRes, filesRes, updatesRes] = await Promise.all([
        supabase.rpc('codevaro_client_portal_project_details_by_session', {
          p_session_token: sessionToken,
          p_project_id: projectId
        }),
        supabase.rpc('codevaro_client_portal_project_files_list_by_session', {
          p_session_token: sessionToken,
          p_project_id: projectId
        }),
        supabase.rpc('codevaro_client_portal_project_updates_list_by_session', {
          p_session_token: sessionToken,
          p_project_id: projectId
        })
      ])

      if (projectRes.error) throw projectRes.error
      if (filesRes.error) throw filesRes.error
      if (updatesRes.error) throw updatesRes.error

      setProject(projectRes.data?.[0] || null)
      setFiles(filesRes.data || [])
      setUpdates(updatesRes.data || [])
    } catch (err) {
      console.error(err)
      setError(err.message || 'Σφάλμα φόρτωσης project.')
    } finally {
      setLoading(false)
    }
  }

  async function handleLogout() {
    try {
      if (sessionToken) {
        await supabase.rpc('codevaro_client_portal_logout', {
          p_session_token: sessionToken
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      localStorage.removeItem(PORTAL_SESSION_KEY)
      setSessionToken('')
      navigate('/portal', { replace: true })
    }
  }

  if (!sessionToken) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Client Portal</h1>
          <p className="text-sm text-gray-600">
            Δεν υπάρχει ενεργό session. Χρησιμοποιήστε το invite link που λάβατε.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div>
            <Link to="/portal" className="text-sm text-blue-600 hover:underline">
              ← Επιστροφή στο Dashboard
            </Link>
            <h1 className="mt-2 text-2xl font-semibold text-gray-900">
              {project?.title || 'Project'}
            </h1>
          </div>

          <button
            onClick={handleLogout}
            className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        ) : !project ? (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-yellow-700">
            Το project δεν βρέθηκε ή δεν είναι διαθέσιμο στο portal.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Στοιχεία Project</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InfoRow label="Status" value={project.status} />
                <InfoRow label="Priority" value={project.priority} />
                <InfoRow label="Start Date" value={project.start_date} />
                <InfoRow label="Deadline" value={project.deadline} />
                <div className="md:col-span-2">
                  <InfoRow label="Περιγραφή για Portal" value={project.portal_summary || project.description} />
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Αρχεία</h2>
              {files.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν διαθέσιμα αρχεία.</p>
              ) : (
                <div className="space-y-3">
                  {files.map((file) => (
                    <div
                      key={file.file_id}
                      className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{file.file_name}</div>
                        <div className="text-sm text-gray-500">{file.file_type || '—'}</div>
                      </div>

                      <a
                        href={file.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                      >
                        Άνοιγμα
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white p-6 shadow">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Updates</h2>
              {updates.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν διαθέσιμα updates.</p>
              ) : (
                <div className="space-y-4">
                  {updates.map((item) => (
                    <div key={item.update_id} className="rounded-lg border border-gray-200 p-4">
                      <div className="font-medium text-gray-900">{item.title || 'Update'}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {item.created_at ? new Date(item.created_at).toLocaleString('el-GR') : '—'}
                      </div>
                      <div className="mt-3 whitespace-pre-wrap text-sm text-gray-700">
                        {item.content || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{value || '—'}</div>
    </div>
  )
}